import {
  useAsyncFn,
  useCustomCompareEffect,
} from '@pogokid/react-hooks'
import {
  AsyncQueryResponse,
  AsyncSingleResponse,
  QueryResponse,
  ResponseWithRefetch,
  SingleResponse,
} from '@soniq/public-resource'
import { useCallback, useMemo, useRef } from 'react'

export type AsyncSingleResponseFn<Schema> =
  () => Promise<SingleResponse<Schema> | null>

export function useAsyncSingleResponse<Schema>(
  fn: AsyncSingleResponseFn<Schema>,
  dependencies: React.DependencyList,
  options: { enabled: boolean; refetchOnly?: boolean }
): AsyncSingleResponse<Schema> &
  ResponseWithRefetch<Promise<Schema | null>> {
  const countRef = useRef(0)
  const timerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)
  const [state, get] = useAsyncFn(async () => {
    const res = await fn()
    if (res) {
      return res.data
    } else {
      return null
    }
  }, dependencies)

  const refetch =
    useCallback(async (): Promise<Schema | null> => {
      if (countRef.current > 10) {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
        throw new Error(
          'Fetch loop detected, cancelling request'
        )
      }
      if (options.enabled) {
        countRef.current += 1
        // The guard is against a render loop, so the count only builds
        // up while fetches come back to back. A second of quiet — a
        // spaced-out refresh, or a person clicking — clears it.
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
        timerRef.current = setTimeout(() => {
          countRef.current = 0
        }, 1_000)
        return get()
      }
      return null
    }, [get, options.enabled])

  useCustomCompareEffect(
    () => {
      if (!options.refetchOnly) {
        void refetch()
      }
    },
    [options.enabled, options.refetchOnly, refetch],
    (a, b) => a[0] === b[0]
  )

  return useMemo(() => ({ ...state, refetch }), [state, refetch])
}

export type AsyncQueryResponseFn<Schema> =
  () => Promise<QueryResponse<Schema> | null>

export function useAsyncQueryResponse<Schema>(
  fn: AsyncQueryResponseFn<Schema>,
  dependencies: React.DependencyList,
  options: { enabled: boolean; refetchOnly?: boolean }
): AsyncQueryResponse<Schema> & ResponseWithRefetch {
  const countRef = useRef(0)
  const timerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)
  const [state, get] = useAsyncFn(async () => {
    const res = await fn()
    if (res) {
      return res.data
    } else {
      return null
    }
  }, dependencies)

  const refetch = useCallback(() => {
    if (countRef.current > 10) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      throw new Error('Fetch loop detected, cancelling request')
    }
    if (options.enabled) {
      countRef.current += 1
      // Same render-loop guard as `useAsyncSingleResponse`: a second of
      // quiet clears the count.
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(() => {
        countRef.current = 0
      }, 1_000)
      void get()
    }
  }, [get, options.enabled])

  useCustomCompareEffect(
    () => {
      if (!options.refetchOnly) {
        refetch()
      }
    },
    [options.enabled, options.refetchOnly, refetch],
    (a, b) => a[0] === b[0]
  )

  return useMemo(() => ({ ...state, refetch }), [state, refetch])
}
